import React, { useMemo, useCallback } from 'react';
import {
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineExclamationTriangle,
  HiOutlineChartBar,
  HiOutlineHeart,
  HiOutlineBell,
  HiOutlineCheckCircle,
  HiHeart,
  HiBell,
} from 'react-icons/hi2';
import ReactECharts from 'echarts-for-react';

interface SummaryProps {
  productStock: any | null;
  movements: any[];
  shortages: any[];
  mermas: any[];
  isFavorite: boolean;
  isMonitoring: boolean;
  onToggleFavorite: () => void;
  onToggleMonitoring: () => void;
}

const STATUS_CONFIG = {
  critical: { badge: 'mp-badge-danger', label: 'Crítico', Icon: HiOutlineExclamationTriangle },
  warning: { badge: 'mp-badge-warning', label: 'Bajo', Icon: HiOutlineExclamationTriangle },
  healthy: { badge: 'mp-badge-success', label: 'Óptimo', Icon: HiOutlineCheckCircle },
} as const;

const Summary: React.FC<SummaryProps> = ({
  productStock, movements, shortages, mermas,
  isFavorite, isMonitoring, onToggleFavorite, onToggleMonitoring
}) => {
  const currentStock = productStock?.reported_stock ?? productStock?.system_stock ?? 0;
  const minimumStock = productStock?.min_quantity ?? 0;

  const stockStatus: keyof typeof STATUS_CONFIG = currentStock <= minimumStock ? 'critical' : currentStock <= minimumStock * 1.5 ? 'warning' : 'healthy';
  const { badge: badgeClass, label: statusLabel, Icon: StatusIcon } = STATUS_CONFIG[stockStatus];

  const recentMovements = (movements || []).slice(-10);
  const deltas = recentMovements.map((m: any) => (m.new_quantity ?? 0) - (m.old_quantity ?? 0));
  const totalIncoming = deltas.filter((d: number) => d > 0).reduce((a: number, b: number) => a + b, 0);
  const totalOutgoing = Math.abs(deltas.filter((d: number) => d < 0).reduce((a: number, b: number) => a + b, 0));

  const countRecent = (items: any[]) => items.filter((item: any) => {
    const d = new Date(item.local_timestamp || item.timestamp);
    const lastWeek = new Date(); lastWeek.setDate(lastWeek.getDate() - 7);
    return d >= lastWeek;
  }).length;

  const recentShortages = countRecent(shortages || []);
  const recentMermas = countRecent(mermas || []);

  const fmt = useCallback((q: number) => new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(q), []);

  // Chart data
  const sortedMovs = (movements || []).slice().sort((a: any, b: any) => new Date(a.local_timestamp || a.timestamp).getTime() - new Date(b.local_timestamp || b.timestamp).getTime());
  const chartLabels = sortedMovs.map((m: any) => new Intl.DateTimeFormat('es-CL', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(m.local_timestamp || m.timestamp)));
  const chartValues = sortedMovs.map((m: any) => Number(m.new_quantity ?? 0));

  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  const isDark = isBrowser ? ((document.documentElement.getAttribute('data-color-mode') || '').toLowerCase() === 'dark') : false;

  const chartOptions = useMemo(() => {
    const rootEl = isBrowser ? document.documentElement : undefined;
    const styles = isBrowser ? getComputedStyle(rootEl as Element) : undefined;
    const cssVar = (name: string, fallback: string) => (styles?.getPropertyValue(name).trim() || fallback);

    const textColor = isDark ? '#e5e7eb' : '#111827';
    const textMuted = isDark ? 'rgba(148,163,184,0.9)' : 'rgba(55,65,81,0.75)';
    const cardBg = cssVar('--card-color', isDark ? '#0b1220' : '#ffffff');
    const gridColor = isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)';
    const axisLineColor = isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)';
    const primary = '#FC7556';

    return {
      color: [primary],
      backgroundColor: 'transparent',
      animation: true, animationDuration: 1200, animationEasing: 'cubicInOut',
      grid: { left: '3%', right: '3%', top: '8%', bottom: '15%', containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', lineStyle: { color: primary, width: 2, type: 'dashed' }, crossStyle: { color: primary }, label: { backgroundColor: primary, color: '#ffffff', fontWeight: 600, borderRadius: 6, padding: [8, 12] } },
        backgroundColor: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
        borderColor: isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
        borderWidth: 1, borderRadius: 12,
        textStyle: { color: textColor, fontWeight: 500, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
        padding: [12, 16],
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const p = params[0];
          return `<div style="font-weight:600;margin-bottom:8px;color:${textColor}">${p.axisValue}</div>
            <div style="display:flex;align-items:center">
              <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${primary};margin-right:8px;box-shadow:0 0 0 2px ${cardBg}"></span>
              <span style="font-weight:500">Cantidad: </span><span style="font-weight:700">${fmt(p.value)}</span>
            </div>`;
        }
      },
      xAxis: { type: 'category', data: chartLabels, boundaryGap: false, axisLine: { lineStyle: { color: axisLineColor, width: 1 } }, axisTick: { show: false }, axisLabel: { color: textMuted, fontSize: 11, fontWeight: 500, margin: 12 } },
      yAxis: { type: 'value', axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: textMuted, fontSize: 11, fontWeight: 500, margin: 12, formatter: (val: number) => fmt(val) }, splitLine: { lineStyle: { color: gridColor, type: 'dashed', width: 1 } } },
      series: [
        {
          name: 'Cantidad', type: 'line', data: chartValues, smooth: 0.3, connectNulls: true,
          showSymbol: chartValues.length <= 10, symbolSize: 8, symbol: 'circle',
          lineStyle: { width: 3.5, color: primary, shadowColor: `${primary}40`, shadowBlur: 8, shadowOffsetY: 2 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: `${primary}25` }, { offset: 0.7, color: `${primary}10` }, { offset: 1, color: `${primary}05` }] } },
          itemStyle: { color: primary, borderWidth: 3, borderColor: cardBg, shadowColor: `${primary}60`, shadowBlur: 6 },
          emphasis: { scale: true, scaleSize: 12, itemStyle: { shadowBlur: 10, shadowColor: `${primary}80` } },
        },
        ...(minimumStock > 0 ? [{
          name: 'Stock Mínimo', type: 'line', data: chartLabels.map(() => minimumStock), symbol: 'none',
          lineStyle: { color: '#dc3545', width: 2, type: 'dashed' as const }, itemStyle: { color: '#dc3545' },
          markLine: { silent: true, data: [{ yAxis: minimumStock, lineStyle: { color: '#dc3545', width: 2, type: 'dashed' as const }, label: { show: true, position: 'insideEndTop', formatter: `Mín: ${fmt(minimumStock)}`, color: '#dc3545', fontSize: 11, fontWeight: 600, backgroundColor: cardBg, padding: [4, 8], borderRadius: 4, borderColor: '#dc3545', borderWidth: 1 } }] }
        }] : [])
      ]
    } as any;
  }, [chartLabels, chartValues, minimumStock, isDark, isBrowser, fmt]);

  const STATS = [
    { icon: HiOutlineArrowTrendingUp, value: fmt(totalIncoming), label: 'Entradas', color: '#10b981' },
    { icon: HiOutlineArrowTrendingDown, value: fmt(totalOutgoing), label: 'Salidas', color: '#f87171' },
    { icon: HiOutlineExclamationTriangle, value: String(recentShortages), label: 'Quiebres', color: '#f59e0b' },
    { icon: HiOutlineChartBar, value: String(recentMermas), label: 'Mermas', color: '#60a5fa' },
  ];

  const cardStyle: React.CSSProperties = {
    borderRadius: '16px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--card-color)',
    padding: '1.25rem',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Hero card: quantity + status + actions */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--strong-color)' }}>{fmt(currentStock)}</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-color)', opacity: 0.6 }}>{productStock?.unidad_medida_name || 'unidades'}</span>
            <span className={badgeClass} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 }}>
              <StatusIcon style={{ width: 14, height: 14 }} /> {statusLabel}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button className="mp-btn-icon" onClick={onToggleFavorite} title={isFavorite ? 'Remover de favoritos' : 'Agregar a favoritos'}>
              {isFavorite ? <HiHeart style={{ width: 20, height: 20, color: '#FC7556' }} /> : <HiOutlineHeart style={{ width: 20, height: 20 }} />}
            </button>
            <button className="mp-btn-icon" onClick={onToggleMonitoring} title={isMonitoring ? 'Desactivar monitoreo' : 'Activar monitoreo'}>
              {isMonitoring ? <HiBell style={{ width: 20, height: 20, color: '#FC7556' }} /> : <HiOutlineBell style={{ width: 20, height: 20 }} />}
            </button>
          </div>
        </div>

        {/* Metrics row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
          {[
            { label: 'Mín', value: fmt(minimumStock) },
            { label: 'Sistema', value: fmt(productStock?.system_stock ?? 0) },
            { label: 'Real', value: fmt(productStock?.reported_stock ?? 0) },
          ].map(m => (
            <div key={m.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-color)', opacity: 0.45, marginBottom: '4px' }}>{m.label}</div>
              <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--strong-color)' }}>{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
        {STATS.map(s => (
          <div key={s.label} style={{ ...cardStyle, padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <s.icon style={{ width: 20, height: 20, flexShrink: 0, color: s.color }} />
            <div>
              <div style={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.2, color: 'var(--strong-color)' }}>{s.value}</div>
              <div style={{ fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color)', opacity: 0.5 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)' }}>
          <HiOutlineChartBar style={{ width: 16, height: 16, color: 'var(--text-color)', opacity: 0.5 }} />
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-color)' }}>Variación de stock</span>
        </div>
        <div style={{ padding: '1rem' }}>
          {chartValues.length > 0 ? (
            <ReactECharts option={chartOptions} style={{ height: 180 }} notMerge={true} opts={{ renderer: 'svg' }} />
          ) : (
            <div className="mp-empty-state" style={{ padding: '2rem 0' }}>
              <HiOutlineChartBar className="mp-empty-state-icon" />
              <div className="mp-empty-state-title">Sin datos de variación</div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-color)', opacity: 0.5 }}>No hay movimientos en el período seleccionado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Summary;
